import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw, Loader2 } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isReloading: boolean;
}

const RELOAD_KEY = 'error_boundary_reload';
const MAX_RELOAD_ATTEMPTS = 3;
const MIN_RELOAD_INTERVAL = 10000; // 10 segundos

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    isReloading: false,
  };

  private static isChunkLoadError(error: Error): boolean {
    const chunkErrorPatterns = [
      'ChunkLoadError',
      'Failed to fetch dynamically imported module',
      'Importing a module script failed',
      'Loading chunk',
      'Loading CSS chunk',
      'Unable to preload CSS',
      'error loading dynamically imported module',
      'Failed to load module script',
      'Failed to fetch',
      'NetworkError',
    ];
    
    return chunkErrorPatterns.some(pattern => 
      error.name?.includes(pattern) || 
      error.message?.includes(pattern)
    );
  }

  private static isRecoverableError(error: Error): boolean {
    const recoverablePatterns = [
      'Should have a queue',
      'Invalid hook call',
      'Minified React error',
      'removeChild',
      'insertBefore',
      'appendChild',
      'The node to be removed is not a child',
      'Cannot read properties of null',
    ];
    
    return recoverablePatterns.some(pattern => 
      error.message?.includes(pattern)
    );
  }

  private static getReloadData(): { attempts: number; lastReload: number } {
    try {
      const data = sessionStorage.getItem(RELOAD_KEY);
      if (data) {
        return JSON.parse(data);
      }
    } catch {}
    return { attempts: 0, lastReload: 0 };
  }

  private static setReloadData(attempts: number): void {
    sessionStorage.setItem(RELOAD_KEY, JSON.stringify({
      attempts,
      lastReload: Date.now()
    }));
  }

  private static clearReloadData(): void {
    sessionStorage.removeItem(RELOAD_KEY);
  }

  public static getDerivedStateFromError(error: Error): Partial<State> {
    const isChunkError = ErrorBoundary.isChunkLoadError(error);
    const isRecoverable = ErrorBoundary.isRecoverableError(error);
    
    if (isChunkError || isRecoverable) {
      const { attempts, lastReload } = ErrorBoundary.getReloadData();
      const now = Date.now();
      const canReload = 
        attempts < MAX_RELOAD_ATTEMPTS && 
        (now - lastReload) > MIN_RELOAD_INTERVAL;
      
      if (canReload) {
        console.log(`[ErrorBoundary] Auto-reload attempt ${attempts + 1}/${MAX_RELOAD_ATTEMPTS}`);
        ErrorBoundary.setReloadData(attempts + 1);
        return { hasError: false, error: null, isReloading: true };
      }
    }
    
    return { hasError: true, error, isReloading: false };
  }

  public componentDidMount(): void {
    // Limpa contador de reload após carregamento bem-sucedido
    ErrorBoundary.clearReloadData();
  }

  public componentDidUpdate(_: Props, prevState: State): void {
    if (this.state.isReloading && !prevState.isReloading) {
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[ErrorBoundary] Uncaught error:', {
      error: error.message,
      componentStack: errorInfo.componentStack,
      isChunkError: ErrorBoundary.isChunkLoadError(error),
    });
  }

  private handleManualReload = (): void => {
    ErrorBoundary.clearReloadData();
    window.location.reload();
  };

  private handleClearCacheAndReload = (): void => {
    ErrorBoundary.clearReloadData();
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    window.location.href = window.location.href.split('?')[0] + '?_=' + Date.now();
  };

  public render(): ReactNode {
    if (this.state.isReloading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="text-center space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
            <p className="text-lg font-medium text-foreground">Atualizando aplicação...</p>
            <p className="text-sm text-muted-foreground">
              Aguarde enquanto carregamos a versão mais recente
            </p>
          </div>
        </div>
      );
    }

    if (this.state.hasError) {
      const isChunkError = this.state.error && 
        ErrorBoundary.isChunkLoadError(this.state.error);
      
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full space-y-6 text-center">
            <AlertCircle className="h-16 w-16 text-destructive mx-auto" />
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">Algo deu errado</h1>
              <p className="text-muted-foreground">
                {isChunkError 
                  ? 'Houve um problema ao carregar a aplicação. Isso geralmente acontece após uma atualização.'
                  : 'Ocorreu um erro inesperado. Por favor, tente recarregar a página.'
                }
              </p>
            </div>
            
            {this.state.error && (
              <details className="text-left">
                <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
                  Detalhes técnicos
                </summary>
                <pre className="text-xs bg-muted text-muted-foreground p-4 rounded-md overflow-auto mt-2 max-h-32">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={this.handleManualReload} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Recarregar Página
              </Button>
              {isChunkError && (
                <Button 
                  variant="outline" 
                  onClick={this.handleClearCacheAndReload}
                >
                  Limpar Cache e Recarregar
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
